/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useRef, useEffect, useState, useMemo } from "react";
import * as d3 from "d3";
import "./TreeGraph.css";
import { Link } from "react-router-dom";
import { Analytics } from "../../../api/analytics";

const TreeGraph = ({
  data,
  layout,
  selectedNodeId,
  setSelectedNode,
  setSelectedNodeId,
  searchTerm,
}) => {

  const LABELS = {
    U: "Ultimate Parent",
    P: "Parent",
    C: "Child",
  };
  const DESCRIPTIONS = {
    U: ["The single top entity that controls the entire hierarchy."],
    P: [
      "An intermediate entity, positioned directly below the ultimate parent",
      "and always above the child entities.",
    ],
    C: ["The bottom layer entity, controlled by the parent."],
  };

  const containerRef = useRef();
  const svgRef = useRef();
  const zoomTransformRef = useRef(d3.zoomIdentity);

  const [root, setRoot] = useState(null);
  const [tick, setTick] = useState(0);

  const [showLegend, setShowLegend] = useState(false);
  const [legendExpanded, setLegendExpanded] = useState(false);
  const [hoveredNodeData, setHoveredNodeData] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  const getBranchCounts = (node) => {
    let counts = { P: 0, C: 0 };
    const traverse = (n) => {
      if (n.data.type === 'P') counts.P++;
      if (n.data.type === 'C') counts.C++;
      if (n.children) n.children.forEach(traverse);
      if (n._children) n._children.forEach(traverse);
    };
    if (node.children) node.children.forEach(traverse);
    if (node._children) node._children.forEach(traverse);
    return counts;
  };

  const getDescendantCount = (node) => {
    let count = 0;
    if (!node.children && !node._children) return 0;
    const children = node.children || node._children;
    const traverse = (n) => {
      count++;
      const ch = n.children || n._children;
      ch?.forEach(traverse);
    };
    children.forEach(traverse);
    return count;
  };

  const getNodeColor = useMemo(() => {
    const colors = ["#2A3C7B", "#6E82CC", "#AAB7F5"];
    return d3.scaleOrdinal(colors);
  }, []);

  function wrap(text, width) {
    if (layout === "horizontal") return;

    text.each(function () {
      let textNode = d3.select(this),
        words = textNode.text().split(/\s+/).reverse(),
        word,
        line = [],
        lineNumber = 0,
        lineHeight = 1.1,
        y = textNode.attr("y"),
        x = textNode.attr("x"),
        dy = parseFloat(textNode.attr("dy") || 0),
        tspan = textNode.text(null).append("tspan").attr("x", x).attr("y", y).attr("dy", dy + "em");

      while (word = words.pop()) {
        line.push(word);
        tspan.text(line.join(" "));
        if (tspan.node().getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(" "));
          line = [word];
          tspan = textNode.append("tspan").attr("x", x).attr("y", y).attr("dy", ++lineNumber * lineHeight + dy + "em").text(word);
        }
      }
    });
  }

const fitToCenter = (duration = 750) => {
  if (!root || !svgRef.current || !containerRef.current) return;


  const svg = d3
  .select(svgRef.current)
  .attr("id", "mychart");
  
  const g = svg.select("g");

  const bounds = g.node().getBBox();

  const containerWidth = containerRef.current.clientWidth;
  const containerHeight = containerRef.current.clientHeight;

  if (!bounds.width || !bounds.height) return;

  const scale = 0.85 / Math.max(
    bounds.width / containerWidth,
    bounds.height / containerHeight
  );

  const translateX =
    containerWidth / 2 - scale * (bounds.x + bounds.width / 2);

  const translateY =
    containerHeight / 2 - scale * (bounds.y + bounds.height / 2);

  const transform = d3.zoomIdentity
    .translate(translateX, translateY)
    .scale(scale);

  svg
    .transition()
    .duration(duration)
    .call(
      d3.zoom().on("zoom", (event) => {
        g.attr("transform", event.transform);
        zoomTransformRef.current = event.transform;
      }).transform,
      transform
    );
};

  useEffect(() => {
    if (!data) return;
    const hierarchyRoot = d3.hierarchy(data);
    const allNodes = hierarchyRoot.descendants();
    const totalNodeCount = allNodes.length;
    const searchLower = searchTerm?.toLowerCase() || "";

    hierarchyRoot.each(n => {
      n.isMatched = searchLower ? (
        n.data.name.toLowerCase().includes(searchLower) ||
        (n.data.metadata && n.data.metadata.toString().toLowerCase().includes(searchLower))
      ) : false;
    });

    hierarchyRoot.eachAfter(n => {
      n.isPathToMatch = n.isMatched || (n.children && n.children.some(c => c.isPathToMatch));
    });

    const collapseRecursive = (d) => {
      if (d.children) {
        d.children.forEach(collapseRecursive);

        // FIX: n > 50 logic. If count is small, allow 2 levels of expansion.
        let isBeyondGlobalLimit = d.depth >= 2;

        const isCrowdedParent = totalNodeCount < 50 ? false : d.depth === 1 && d.children.length > 6;

        if (!d.isPathToMatch) {
          if (isBeyondGlobalLimit || isCrowdedParent) {
            d._children = d.children;
            d.children = null;
          }
        }
      }
    };

    collapseRecursive(hierarchyRoot);
    setRoot(hierarchyRoot);

    const selectedNode = allNodes.find(n => n.data.name === selectedNodeId);
    if (selectedNode) {
      const counts = getBranchCounts(selectedNode);
      setHoveredNodeData({
        name: selectedNode.data.name,
        glueId: selectedNode.data.metadata || "N/A",
        pCount: counts.P,
        cCount: counts.C
      });
    }
  }, [data, searchTerm, selectedNodeId]);

  useEffect(() => {
    if (!root) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    const g = svg.append("g");

    const zoom = d3.zoom()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => {
        zoomTransformRef.current = event.transform;
        g.attr("transform", event.transform);
      });

    // svg.attr("width", "100%").attr("height", "600px").call(zoom);
    const containerHeight = containerRef.current?.clientHeight || 800;

    svg
      .attr("width", "100%")
      .attr("height", containerHeight)
      .call(zoom);

    const treeLayout = d3.tree().nodeSize(layout === "vertical" ? [100, 150] : [50, 250]);
    treeLayout(root);

    const nodes = root.descendants();
    const links = root.links();

    const radius = 14;
    const typeFontSize = "16px";
    const labelFontSize = "12px";

    if (zoomTransformRef.current.k === 1 && zoomTransformRef.current.x === 0) {
      setTimeout(() => fitToCenter(0), 10);
    } else {
      g.attr("transform", zoomTransformRef.current);
    }

    const linkGen = layout === "vertical"
      ? d3.linkVertical().x(d => d.x).y(d => d.y)
      : d3.linkHorizontal().x(d => d.y).y(d => d.x);

    g.selectAll(".link")
      .data(links)
      .join("path")
      .attr("class", "link")
      .attr("fill", "none")
      .attr("stroke", d => d.source.depth === 0 ? "#7AA2FF" : "#8FA4D6")
      .attr("stroke-width", d => d.source.depth === 0 ? 2 : 1.2)
      .attr("opacity", d => d.source.depth === 0 ? 0.85 : 0.45)
      .attr("d", linkGen);

    const node = g.selectAll(".node")
      .data(nodes)
      .join("g")
      .attr("class", "node")
      .style("cursor", "pointer")
      .attr("transform", d => layout === "vertical" ? `translate(${d.x},${d.y})` : `translate(${d.y},${d.x})`);

    node.on("click", (e, d) => {
      e.stopPropagation();
      if (d.children) {
        d._children = d.children;
        d.children = null;
      } else if (d._children) {
        d.children = d._children;
        d._children = null;
      }
      setTick(t => t + 1);
    })
      .on("mouseover", (event, d) => {
        setHoveredId(d.data.name);
        const branchCounts = getBranchCounts(d);
        setHoveredNodeData({
          name: d.data.name,
          glueId: d.data.metadata || "N/A",
          pCount: branchCounts.P,
          cCount: branchCounts.C
        });
      });

    node.each(function (d) {
      const group = d3.select(this);
      const isSelected = d.data.name === selectedNodeId;
      const isHovered = d.data.name === hoveredId;
      const totalDescendants = getDescendantCount(d);

      // Base Node Circle
      group.append("circle")
        .attr("r", radius)
        .attr("fill", getNodeColor(d.data.type))
        // Fixed Stroke Logic: If hovered/matched use those colors, else no stroke on base circle
        .attr("stroke", isHovered ? "#753493" : (d.isMatched ? "#FFD700" : "none"))
        .attr("stroke-width", isHovered || d.isMatched ? 4 : 0);

      // FIXED: Red Dotted Node Selection
      // We append a second circle slightly larger so it wraps the node clearly
      if (isSelected) {
        group.append("circle")
          .attr("r", radius + 4)
          .attr("fill", "none")
          .attr("stroke", "#B52B2B")
          .attr("stroke-width", 3)
          .attr("stroke-dasharray", "4 2")
          .style("pointer-events", "none");
      }

      group.append("text")
        .attr("dy", "0.35em")
        .attr("text-anchor", "middle")
        .style("fill", "#fff")
        .style("font-weight", "bold")
        .style("font-size", typeFontSize)
        .text(d.data.type);

      if (totalDescendants > 0) {
        group.append("text")
          .attr("x", radius - 2)
          .attr("y", -(radius - 5))
          .attr("text-anchor", "start")
          .style("fill", "#2A3C7B")
          .style("font-size", "11px")
          .style("font-weight", "900")
          .text(totalDescendants);
      }

      const hasChildren = d.children || d._children;
      if (hasChildren) {
        group.append("text")
          .attr("y", radius + 12)
          .attr("text-anchor", "middle")
          .style("font-size", "14px")
          .style("font-weight", "900")
          .style("fill", "#2A3C7B")
          .text(d._children ? "+" : "−");
      }

      const label = group.append("text")
        .attr("class", "node-label")
        .style("font-size", labelFontSize)
        .style("font-family", "Arial, sans-serif")
        .style("font-weight", d.isMatched ? "800" : "500")
        .text(d.data.name);

      if (layout === "vertical") {
        label.attr("y", radius + 19).attr("x", 0).attr("dy", "1em").attr("text-anchor", "middle");
      } else {
        label.attr("x", radius + 8).attr("y", 0).attr("dy", "0.35em").attr("text-anchor", "start");
      }
      label.call(wrap, 100);
    });
  }, [root, tick, layout, selectedNodeId, searchTerm, hoveredId]);

  const handleZoom = (sc) => {
    const svg = d3.select(svgRef.current);
    const zoom = d3.zoom().on("zoom", (e) => {
      svg.select("g").attr("transform", e.transform);
      zoomTransformRef.current = e.transform;
    });
    svg.transition().duration(300).call(zoom.scaleBy, sc);
  };

  const globalTypeCounts = useMemo(() => {
    const counts = { U: 0, P: 0, C: 0 };
    const traverse = (n) => {
      if (n.type && counts[n.type] != null) counts[n.type]++;
      n.children?.forEach(traverse);
    };
    if (data) traverse(data);
    return counts;
  }, [data]);

  return (
    <div ref={containerRef} className="tree-graph-container">
      <svg ref={svgRef} className="bg-white" />

      <div className="fixed-zoom-controls">
        <button onClick={() => handleZoom(1.3)} title="Zoom In">+</button>
        <button onClick={() => handleZoom(0.7)} title="Zoom Out">−</button>
        <button onClick={() => fitToCenter(750)} title="Center Graph" className="reset-btn">⟳</button>
        <button
          onClick={() => setShowLegend(!showLegend)}
          title="Information"
          className={`info-btn ${showLegend ? 'active' : ''}`}
        >i</button>

        {showLegend && (
          <div className="graph-legend-box" onMouseEnter={() => setLegendExpanded(true)} onMouseLeave={() => setLegendExpanded(false)}>
            <div className="legend-header">
              <span className="legend-title">Legal Entity Hierarchy Layers</span>
              <button className="legend-close" onClick={() => setShowLegend(false)}>×</button>
            </div>
            <div className="legend-items">
              {["U", "P", "C"].map((type) => (
                <div key={type} className="legend-icon-item">
                  <div
                    className="legend-circle"
                    style={{ backgroundColor: getNodeColor(type) }}
                  >
                    {type}
                  </div>
                  <span className="legend-label">
                    : <strong>{globalTypeCounts[type]}</strong>
                  </span>

                  {/* The Tooltip Div */}
                  <div className="legend-tooltip">
                    <div className="tooltip-title">{LABELS[type]}</div>
                    {DESCRIPTIONS[type].map((line, i) => (
                      <div key={i} className="tooltip-line">
                        {line}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div className="legend-total-wrapper">
                <span className="legend-total">
                  <strong>Total Entities: {Object.values(globalTypeCounts).reduce((a, b) => a + b, 0)}</strong>
                </span>
              </div>
            </div>
            {legendExpanded && (
              <div className="legend-instructions">
                <ul>
                  <li>Hover on any entity to view full name and Glue ID.</li>
                  <li>Click on <span className="legend-node-tag">U</span> or <span className="legend-node-tag">P</span> to show/slide/hide child entities.</li>
                  <li>Selected entity is highlighted with dashed purple outline.</li>
                  <li>Searched entity is highlighted with dashed red outline.</li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="side-panel-container">
        {hoveredNodeData && (
          <div className="hover-detail-panel">
            {/* <div className="legend-header">
              <span className="legend-title">Details for Entity Hierarchy Layers</span>
            </div> */}
            <div className="detail-row"><strong>Name:</strong> {hoveredNodeData.name}</div>
            <div className="detail-row"><strong>Glue ID:</strong> {hoveredNodeData.glueId}</div>
            <Link
              className="link-style"
              to={`/summary/${encodeURIComponent(hoveredNodeData.glueId)}`}
              // onClick={() => Analytics.logEvent('graph_summary_node_clicked', { glueId: hoveredNodeData.glueId })}
            >View Summary →</Link>
          </div>
        )}
      </div>

      <style>{`
        .tree-graph-container { position: relative; border: 1px solid #eee; overflow: hidden; background: #fff; }
        
        .fixed-zoom-controls { 
            position: absolute; 
            bottom: 150px; 
            right: 20px; 
            display: flex; 
            flex-direction: column; 
            gap: 8px; 
            z-index: 101;
        }

        .fixed-zoom-controls button { 
            width: 40px; 
            height: 40px; 
            background: white; 
            border: 1px solid #ddd; 
            border-radius: 8px; 
            cursor: pointer; 
            font-weight: bold; 
            color: #2A3C7B; 
            font-family: serif; 
            font-size: 18px;
            box-shadow: 0 2px 5px rgba(0,0,0,0.1);
        }

        .info-btn.active { background: #2A3C7B; color: white; }

        .graph-legend-box {
            position: absolute;
            bottom: 0;
            right: 50px;
            width: 280px;
            background: white;
            padding: 15px;
            border-radius: 12px;
            box-shadow: 0 4px 15px rgba(0,0,0,0.15);
            border: 1px solid #e0e0e0;
            animation: fadeIn 0.2s ease-out;
        }

        .legend-header { 
            display: flex; 
            justify-content: space-between; 
            align-items: center;
            margin-bottom: 10px; 
            font-weight: bold; 
            border-bottom: 1px solid #eee; 
            padding-bottom: 5px; 
            font-size: 13px;
        }

        .legend-close { 
            border: none !important; 
            background: none !important; 
            padding: 0 !important; 
            width: auto !important; 
            height: auto !important; 
            font-size: 20px !important; 
            color: #999 !important; 
            box-shadow: none !important;
        }

        .legend-items { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
        .legend-icon-item { display: flex; align-items: center; gap: 4px; }
        .legend-circle { width: 22px; height: 22px; border-radius: 50%; color: white; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: bold; }
        .legend-label { font-size: 12px; }
        .legend-total-wrapper { width: 100%; border-top: 1px solid #f0f0f0; padding-top: 5px; font-size: 12px; }
        
        .legend-instructions { font-size: 11px; color: #666; margin-top: 10px; border-top: 1px dotted #ccc; padding-top: 8px; }
        .legend-instructions ul { padding-left: 15px; margin: 0; }
        .legend-node-tag { background: #eee; padding: 0 4px; border-radius: 3px; font-weight: bold; }

        .side-panel-container {
            position: absolute;
            top: 20px;
            right: 20px;
            display: flex;
            flex-direction: column;
            gap: 10px;
            z-index: 100;
            width: 300px;
        }

        .hover-detail-panel {
            background: white;
            padding: 10px;
            border-radius: 12px;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
            border: 1px solid #e0e0e0;
            animation: fadeIn 0.2s ease-in-out;
            font-size: 13px;
            width: 290px;
        }
        
        .link-style { color: #2A3C7B; font-weight: bold; text-decoration: none; display: block; margin-top: 10px; }

        @keyframes fadeIn {
            from { opacity: 0; transform: translateX(10px); }
            to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default TreeGraph;